import {kebabCase} from 'lodash-es';
import React, {useEffect, useState} from 'react';

import {Button, FilePicker, Modal} from '@/components';
import {fileService, kycBucketKey} from '@/services/fileService';
import {liveAccessService} from '@/services/liveAccessService';
import type {BusinessLiveAccessTemplateView} from '@/types';
import {BusinessLiveAccessDocumentType} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import {
  isAllowedLiveAccessFile,
  isWithinLiveAccessMaxSize,
  liveAccessDocLabel,
} from './liveAccessStatus';

// The default templates every business downloads, one per documentType. The
// agreement is downloaded from the Pricing page when the business does not
// sign it in the portal.
const TEMPLATE_TYPES = [
  BusinessLiveAccessDocumentType.Uat,
  BusinessLiveAccessDocumentType.BusinessAgreement,
];

type ManageTemplatesModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ManageTemplatesModal = ({isOpen, onClose}: ManageTemplatesModalProps) => {
  const [templates, setTemplates] = useState<BusinessLiveAccessTemplateView[]>(
    [],
  );
  const [stagedFiles, setStagedFiles] = useState<Record<string, File>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Start "not loaded" so the required-hint / coverage checks don't flash before
  // the existing templates are fetched.
  const [hasLoaded, setHasLoaded] = useState(false);

  const load = async () => {
    try {
      setTemplates(await liveAccessService.getTemplates());
    } catch (e) {
      showErrorMessage(getErrorMessage(e, 'Error loading templates'));
    } finally {
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setStagedFiles({});
      setHasLoaded(false);
      // `load` toasts its own failures, so this only fires if that catch or
      // the finally itself throws. Log rather than swallow silently.
      load().catch(logRejection('Failed to load live access templates'));
    }
  }, [isOpen]);

  const stageFile = (
    documentType: BusinessLiveAccessDocumentType,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selected = e.target.files?.[0] ?? null;
    if (!selected) {
      return;
    }
    if (!isAllowedLiveAccessFile(documentType, selected)) {
      const isUat = documentType === BusinessLiveAccessDocumentType.Uat;
      showErrorMessage(`Only ${isUat ? 'Excel' : 'PDF'} files are allowed.`);
      e.target.value = '';
      return;
    }
    if (!isWithinLiveAccessMaxSize(selected)) {
      showErrorMessage('File size must be 10MB or less.');
      e.target.value = '';
      return;
    }
    setStagedFiles((prev) => ({...prev, [documentType]: selected}));
  };

  const hasStaged = Object.keys(stagedFiles).length > 0;
  // A type is "covered" if it already has a saved template or a file is staged
  // for it now. Only the UAT must be covered before saving: without a default
  // agreement the Pricing page still takes the business's signed copy, so the
  // agreement never holds up a UAT update.
  const isCovered = (type: BusinessLiveAccessDocumentType) =>
    templates.some((t) => t.documentType === type) || !!stagedFiles[type];
  const canSave =
    hasLoaded && hasStaged && isCovered(BusinessLiveAccessDocumentType.Uat);

  // Single submit for the whole modal: upload whichever templates were staged
  // (one or both) in one action.
  const handleSave = async () => {
    const entries = Object.entries(stagedFiles) as [
      BusinessLiveAccessDocumentType,
      File,
    ][];
    if (!entries.length) {
      return;
    }
    setIsSaving(true);
    try {
      await Promise.all(
        entries.map(async ([documentType, file]) => {
          const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
          const s3Key = `live-access/templates/${kebabCase(documentType)}-${Date.now()}${
            ext ? `.${ext}` : ''
          }`;
          await fileService.uploadFile(s3Key, kycBucketKey, undefined, file);
          await liveAccessService.upsertTemplate({
            documentType,
            label: liveAccessDocLabel(documentType),
            s3Key,
          });
        }),
      );
      showSuccessMessage(
        entries.length > 1 ? 'Templates updated' : 'Template updated',
      );
      setStagedFiles({});
      await load();
    } catch (e) {
      showErrorMessage(getErrorMessage(e, 'Error updating templates'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage default templates"
      width="w-10/12 sm:w-10/12 md:w-8/12 lg:w-5/12"
      closeOnOutsideClick
      dataCy="live-access-templates-modal"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            colorScheme="cyan"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            colorScheme="cyan"
            isLoading={isSaving}
            loadingText="Saving..."
            disabled={!canSave}
            dataCy="live-access-templates-save"
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {TEMPLATE_TYPES.map((type) => {
          const existing = templates.find((t) => t.documentType === type);
          const staged = stagedFiles[type];
          const isUat = type === BusinessLiveAccessDocumentType.Uat;
          return (
            <div
              key={type}
              data-cy={`live-access-template-${type}`}
              className="flex flex-col gap-2 border border-gray-200 dark:border-dark-borderMid rounded-md p-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-100">
                  {liveAccessDocLabel(type)}
                  {hasLoaded && isUat && !isCovered(type) && (
                    <span
                      className="ml-1 text-red-500"
                      data-cy="live-access-template-required"
                    >
                      (required)
                    </span>
                  )}
                </span>
                {existing?.url && (
                  <a
                    href={existing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-cyan-600 underline"
                    data-cy="live-access-template-view-current"
                  >
                    View current
                  </a>
                )}
              </div>
              <FilePicker
                key={staged ? staged.name : `empty-${type}`}
                name={`template-file-${type}`}
                dataCy={`live-access-template-${type}`}
                label={isUat ? 'Template file (Excel)' : 'Template file (PDF)'}
                accept={
                  isUat
                    ? '.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    : 'application/pdf'
                }
                isLoading={false}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  stageFile(type, e)
                }
              />
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default ManageTemplatesModal;
